import React from "react";
import PropTypes from "prop-types";
import {
  useTheme,
  Divider,
  Flex,
  Image,
  Heading,
  Text,
  Box,
  Link,
} from "@chakra-ui/core";
import get from "lodash/get";
import Breadcrumbs from "../../components/Breadcrumbs";
import DefaultLayout from "../../components/layouts/DefaultLayout";
import PercentWheel from "../../components/charts/percentWheel/PercentWheel";
import Picture from "../../components/Picture";
import ShareSocialMedia from "../../components/ShareSocialMedia";
import SecondaryButton from "../../components/buttons/SecondaryButton";

const breadcrumbs = [
  "Orchid",
  "Resources",
  "What you'll learn from your report",
];

export async function getServerSideProps(context) {
  return {
    props: {
      hostname: get(context, "req.headers.host"),
    },
  };
}

function Page({ hostname }) {
  const theme = useTheme();

  return (
    <DefaultLayout>
      <Flex
        direction="column"
        alignItems="center"
        px={["0", "0", "20px"]}
        py="48px"
        mx="auto"
        maxWidth="1240px"
        w="100%"
      >
        <Flex
          direction="row"
          justifyContent={["center", "center", "space-between"]}
          alignItems="flex-start"
          w="100%"
          mb={["64px", "64px", "0"]}
        >
          <Breadcrumbs alignSelf="flex-start" breadcrumbs={breadcrumbs} />
          <ShareSocialMedia hostname={hostname} />
        </Flex>
        <Flex direction="row" mb="24px">
          <Image h="25px" htmlHeight="15px" w="100px" src="/logo.svg" />
          <Divider mx="10px" orientation="vertical" />
          <Heading fontWeight="300" fontSize="20px" color="orchidBlue500">
            Resources
          </Heading>
        </Flex>
        <Heading
          fontSize={["24px", "40px"]}
          fontWeight="300"
          color="orchidBlue500"
          mb="24px"
          textAlign="center"
        >
          What you’ll learn from your Couple Report
        </Heading>
        <Flex direction="row" mb={["56px", "80px"]}>
          <Text fontSize={["14px", "16px"]} color="orchidGrey500">
            5 min read
          </Text>
          <Divider orientation="vertical" />
          <Text fontSize={["14px", "16px"]} color="orchidPurple">
            Christina Ren
          </Text>
        </Flex>
        <Box w="100%" px={["24px", 0]} mb={["24px", "48px"]}>
          <Box pt={["30px", "0"]} backgroundColor="#BBC9DC">
            <Picture
              mx="auto"
              imagePath="/images/resources/mother-child.png"
              alt="mother-child"
              imgProps={{ style: { width: "auto", margin: "0 auto" } }}
              hasMobile
            />
          </Box>
        </Box>
        <Box w="100%" maxW="792px" mx="auto" px={["24px", "0"]}>
          <Flex
            direction={["column", "row"]}
            alignItems={["center", "flex-start"]}
            mb="43px"
          >
            <Box mr={[0, "48px"]} w={["90px", "128px"]} flexShrink="0">
              <Image
                src="/images/resources/christina.png"
                alt="christina"
                style={{ clipPath: "circle(40%)" }}
              />
            </Box>
            <Flex direction="column">
              <Text
                fontFamily="heading"
                fontWeight="300"
                fontSize={["18px", "20px"]}
                color="orchidGrey700"
                mb="49px"
              >
                The purpose of this Couple Report is to tell you about possible
                genetic predispositions of your future child. Before your
                results, we’ll walk you through a few key concepts to help to
                help you understand your report so that you know what to expect.
              </Text>
              <Flex
                direction={["column", "column", "row"]}
                justifyContent="space-between"
              >
                <Text fontSize={["14px", "16px"]} mb={["18px", "18px", "0"]}>
                  <Text color="orchidPurple" d="inline">
                    Christina Ren
                  </Text>
                  , Certified Genetic Counselor
                </Text>
                <Box>
                  <Link
                    fontSize="12px"
                    color="orchidGrey500"
                    href="https://twitter.com/RenChristina"
                    target="_blank"
                    rel="noopener noreferrer"
                    mr="24px"
                  >
                    / Twitter
                  </Link>
                  <Link
                    fontSize="12px"
                    color="orchidGrey500"
                    href="https://www.linkedin.com/in/christinaren1/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    / LinkedIn
                  </Link>
                </Box>
              </Flex>
            </Flex>
          </Flex>
          <Divider
            orientation="horizontal"
            borderColor="orchidGrey200"
            mb={["24px", "43px"]}
          />
        </Box>
        <NarrowContainer>
          <StyledHeading>What is a “common condition”?</StyledHeading>
          <StyledText>
            Common conditions are considered those that are observed frequently
            in a population. Common conditions like heart disease and diabetes
            are often caused by a combination of genetic and environmental
            factors.
          </StyledText>
          <StyledText>
            In our test, we look at common conditions that can affect the heart,
            brain, and the child’s overall health. Beyond “common” conditions,
            there are also “rare” genetic conditions that this test does not
            evaluate.
          </StyledText>
          <StyledHeading>
            How are the genetics of common conditions determined?
          </StyledHeading>
          <StyledText>
            We look at many specific genetic variants associated with each
            common condition. While individually each of these genetic variants
            might only have a small effect, their combined effect can be large.
            The “genetic risk score” is our estimate for how much those variants
            might matter for your potential child.
          </StyledText>
          <StyledText>
            You can think of each of these variants as a marble in a jar, with
            the amount of marbles representing your child’s genetic
            susceptibility to certain conditions.
          </StyledText>
        </NarrowContainer>
        <Picture
          mx="auto"
          mb="32px"
          imagePath="/images/resources/jar-1.png"
          alt="genetic risk factor"
          hasMobile
        />
        <NarrowContainer>
          <StyledHeading>What is genetic risk?</StyledHeading>
          <StyledText>
            Differences in our DNA result in different genetic susceptibility to
            certain conditions.
          </StyledText>
        </NarrowContainer>
        <Picture
          mx="auto"
          mb="32px"
          imagePath="/images/resources/jar-2.png"
          alt="jar with marbles"
          hasMobile
        />
        <NarrowContainer>
          <StyledHeading>
            How do we estimate your future child’s genetic risks from your DNA?
          </StyledHeading>
          <StyledText position="relative">
            Sometimes, genetic risk factors linked to increased risk for
            developing a condition{" "}
            <span style={{ fontWeight: "bold" }}>
              cluster unevenly in one individual, just by random chance.
            </span>
            <Image
              display={["none", "none", "none", "block"]}
              position="absolute"
              top="60%"
              left="80%"
              src="/images/resources/squirly-arrow.png"
              alt="arrow pointing down"
            />
          </StyledText>
        </NarrowContainer>
        <Picture
          mx="auto"
          mb="32px"
          imagePath="/images/resources/parents.png"
          alt="parents"
          hasMobile
        />
        <NarrowContainer>
          <StyledText>
            We run many simulations of the ways you and your partners’ DNA (and
            associated genetic risks) can combine. This gives us a sense of
            which disease risks might be elevated in your potential child or
            children. We then report to you how often we see risk-increasing
            combinations of genetic variants.
          </StyledText>
          <StyledText>
            Think of this statistical modeling as us sorting through many jars
            of marbles that each represent unique DNA combinations your child
            could inherit.
          </StyledText>
        </NarrowContainer>
        <Picture
          mx="auto"
          mb="32px"
          imagePath="/images/resources/jar-3.png"
          alt="jar with marbles"
          hasMobile
        />
        <NarrowContainer>
          <StyledHeading>
            What is the chance you could have a child with elevated genetic risk
            for any of these conditions tested?
          </StyledHeading>
          <Divider orientation="horizontal" color="orchidGrey200" my="43px" />
          <Flex direction="row">
            <Box mr="43px">
              <PercentWheel
                width="78px"
                chanceElevated="68"
                strokeColor={theme.colors.green}
                textColor="transparent"
              />
            </Box>
            <Box>
              <Heading
                color="orchidBlue500"
                fontSize="20px"
                fontWeight="300"
                mb="14px"
              >
                Most couples receive a normal result
              </Heading>
              <StyledText>
                meaning your future child’s risk of developing the conditions
                tested is similar to the general population
              </StyledText>
            </Box>
          </Flex>
          <Divider orientation="horizontal" color="orchidGrey200" my="43px" />
          <Flex direction="row" mb="48px">
            <Box mr="43px">
              <PercentWheel
                width="78px"
                chanceElevated="32"
                offsetPercent="68"
                strokeColor={theme.colors.red}
                textColor="transparent"
              />
            </Box>
            <Box>
              <Heading
                color="orchidBlue500"
                fontSize="20px"
                fontWeight="300"
                mb="14px"
              >
                Some couples receive an elevated genetic risk result
              </Heading>
              <StyledText>
                meaning the chance your future child has an increased genetic
                predisposition to specific conditions is higher.
              </StyledText>
            </Box>
          </Flex>
          <StyledHeading>
            If your future child has an elevated genetic risk for a condition,
            then the risk of developing that condition at some point in their
            life, also known as lifetime risk, is higher than in the general
            population.
          </StyledHeading>
        </NarrowContainer>
        <Box w="100%" position="relative">
          <Picture
            breakpoint={theme.breakpoints[1]}
            mx="auto"
            mb="32px"
            imagePath="/images/resources/other-conditions-we-test-for.png"
            alt="other conditions we test for"
            hasMobile
          />
          <Text
            fontSize={["2.9vw", "2.9vw", "0.9vw", "0.9vw", "12px"]}
            top={["113vw", "113vw", "29.5vw", "29.5vw", "375px"]}
            maxW={["86vw", "86vw", "42vw", "42vw", "511px"]}
            left={[
              "7vw",
              "7vw",
              "calc(50% - 20vw)",
              "calc(50% - 20vw)",
              "calc(50% - 255px)",
            ]}
            color="orchidGrey400"
            position="absolute"
            borderColor="orchidGrey100"
          >
            *Here, we refer to coronary artery disease as heart disease.
            Lifetime risk varies based on sex; females on average have a 19.2%
            chance of developing coronary artery disease by the age of 80, which
            is lower than for men (
            <Link
              href="https://www.ahajournals.org/doi/10.1161/CIRCULATIONAHA.105.548206"
              target="_blank"
              rel="noopener noreferrer"
              textDecoration="underline"
            >
              Lloyd-Jones, D.M., et al., 2006
            </Link>
            ).
          </Text>
        </Box>
        <NarrowContainer>
          <StyledText>
            Given the same lifestyle choices or environmental exposures, an
            individual with elevated genetic risk is more likely to have an
            overflowing jar, representing the development of a condition,
            compared to an individual with normal genetic risk.
          </StyledText>
        </NarrowContainer>
        <Picture
          mx="auto"
          mb="32px"
          imagePath="/images/resources/compare-1.png"
          alt="comparing jars"
          hasMobile
        />
        <NarrowContainer>
          <StyledText>
            However, having an elevated genetic risk does not mean you or your
            child will get that condition. Your unique lifestyle choices and
            environmental exposures can also impact whether you’ll be affected.
            For example, someone with the same genetic risk as you can develop a
            condition while you do not because you may have made more optimal
            lifestyle choices.
          </StyledText>
        </NarrowContainer>
        <Picture
          mx="auto"
          mb="32px"
          imagePath="/images/resources/compare-2.png"
          alt="comparing jars"
          hasMobile
        />
        <NarrowContainer>
          <Flex direction={["column", "row"]} alignItems="flex-start" mb="32px">
            <StyledText mr="22px">
              Learn more about the basics of genetic risk scores through a
              visual explainer.
            </StyledText>
            <Link
              href="https://polygenicscores.org/explained/"
              rel="noopener noreferrer"
              target="_blank"
              w={["100%", "auto"]}
            >
              <SecondaryButton w="100%">Learn More</SecondaryButton>
            </Link>
          </Flex>
          <StyledHeading>What can you do about your results?</StyledHeading>
          <StyledText mb="48px">
            Knowing if your children may be at an increased genetic risk for
            certain common conditions empowers you to be proactive about your
            reproductive health and plan for the future.
          </StyledText>
          <Flex direction="row">
            <Box w={["90px", "auto"]} flexShrink="0" mr={["16px", "32px"]}>
              <Image
                mx="auto"
                src="/images/resources/general-health.png"
                alt="general health"
              />
            </Box>
            <Box>
              <Heading
                color="orchidBlue500"
                fontSize="20px"
                fontWeight="300"
                mb="18px"
              >
                Early prevention and treatment
              </Heading>
              <StyledText>
                Increased awareness, screening, and lifestyle modifications can
                help prevent or identify conditions early.
              </StyledText>
            </Box>
          </Flex>
          <Divider orientation="horizontal" color="orchidGrey200" my="32px" />
          <Flex direction="row">
            <Box w={["90px", "auto"]} flexShrink="0" mr={["16px", "32px"]}>
              <Image
                mx="auto"
                src="/images/resources/babyfeet.png"
                alt="general health"
              />
            </Box>
            <Box>
              <Heading
                color="orchidBlue500"
                fontSize="20px"
                fontWeight="300"
                mb="18px"
              >
                Embryo screening
              </Heading>
              <StyledText>
                If desired, screening your embryos created through in vitro
                fertilization (IVF) can potentially reduce your child’s genetic
                risk
              </StyledText>
            </Box>
          </Flex>
        </NarrowContainer>
      </Flex>
    </DefaultLayout>
  );
}

const StyledText = ({ children, ...rest }) => (
  <Text
    mb={["24px", "32px"]}
    fontSize={["16px", "18px"]}
    lineHeight="32px"
    color="orchidGrey500"
    {...rest}
  >
    {children}
  </Text>
);

const StyledHeading = ({ children, ...rest }) => (
  <Heading
    mb={["16px", "32px"]}
    fontSize={["20px", "32px"]}
    fontWeight="300"
    color="orchidBlue"
    {...rest}
  >
    {children}
  </Heading>
);

const NarrowContainer = ({ children, ...rest }) => (
  <Box w="100%" maxW="612px" mx="auto" px={["24px", "24px", "0"]} {...rest}>
    {children}
  </Box>
);

StyledText.propTypes = { children: PropTypes.node };
StyledHeading.propTypes = { children: PropTypes.node };
NarrowContainer.propTypes = { children: PropTypes.node };

Page.propTypes = {
  hostname: PropTypes.string,
};

export default Page;
